(() => {
  'use strict';

  const DRAWER_SELECTOR = '#t4s-menu-drawer';
  const TEMPLATE_SELECTOR = '#axa-mobile-menu-template';
  const SHELL_SELECTOR = ':scope > [data-axa-shell-node]';
  const STORAGE_PREFIX = 'axa-mobile-drawer-tab:';
  const COUNT_EVENTS = [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    't4s:cart:updated',
    'wishlist:updated',
    't4s:wishlist:updated'
  ];

  const state = {
    activeTab: null,
    bodyOverflow: '',
    countFrame: 0,
    drawer: null,
    drawerBound: null,
    drawerObserver: null,
    drawerObserved: null,
    headerObserver: null,
    headerObserved: null,
    lastHidden: 'true',
    lastOpener: null,
    lockedByAxa: false,
    media: null,
    mediaListener: null,
    mediaQuery: '',
    template: null
  };

  const toBoolean = (value) => value === true || value === 'true';

  const directChildren = (parent, selector) => {
    if (!parent) return [];
    return Array.from(parent.children).filter((element) => element.matches(selector));
  };

  const firstDirectChild = (parent, selector) => directChildren(parent, selector)[0] || null;

  const findTemplate = () => document.querySelector(TEMPLATE_SELECTOR);
  const findDrawer = () => document.querySelector(DRAWER_SELECTOR);

  const config = () => {
    const data = state.template ? state.template.dataset : {};
    const parsedBreakpoint = Number.parseInt(data.breakpoint || '1020', 10);

    return {
      breakpoint: Number.isFinite(parsedBreakpoint) ? parsedBreakpoint : 1020,
      defaultTab: data.defaultTab === 'tab2' ? 'tab2' : 'tab1',
      lockScroll: toBoolean(data.lockScroll),
      rememberTab: toBoolean(data.rememberTab),
      resetOnOpen: toBoolean(data.resetOnOpen),
      sectionId: data.sectionId || 'axa-mobile-drawer',
      showClose: toBoolean(data.showClose),
      singleOpenTab1: toBoolean(data.singleOpenTab1),
      singleOpenTab2: toBoolean(data.singleOpenTab2)
    };
  };

  const storageKey = () => `${STORAGE_PREFIX}${config().sectionId}`;

  const rememberedTab = () => {
    if (!config().rememberTab) return null;

    try {
      const value = window.sessionStorage.getItem(storageKey());
      return value === 'tab1' || value === 'tab2' ? value : null;
    } catch (_error) {
      return null;
    }
  };

  const rememberTab = (tabName) => {
    if (!config().rememberTab) return;

    try {
      window.sessionStorage.setItem(storageKey(), tabName);
    } catch (_error) {
      /* Storage can be unavailable in privacy-restricted browsers. */
    }
  };

  const isMobile = () => Boolean(state.media && state.media.matches);
  const isOpen = () => Boolean(state.drawer && state.drawer.getAttribute('aria-hidden') === 'false');

  const removeMediaListener = () => {
    if (!state.media || !state.mediaListener) return;

    if (state.media.removeEventListener) state.media.removeEventListener('change', state.mediaListener);
    else if (state.media.removeListener) state.media.removeListener(state.mediaListener);

    state.mediaListener = null;
  };

  const installMediaQuery = () => {
    if (!state.template) return;

    const query = `(max-width: ${config().breakpoint}px)`;
    if (state.media && state.mediaQuery === query) return;

    removeMediaListener();
    state.mediaQuery = query;
    state.media = window.matchMedia(query);
    state.mediaListener = () => refresh(false);

    if (state.media.addEventListener) state.media.addEventListener('change', state.mediaListener);
    else if (state.media.addListener) state.media.addListener(state.mediaListener);
  };

  const shellNodes = () => {
    if (!state.drawer) return [];

    try {
      return Array.from(state.drawer.querySelectorAll(SHELL_SELECTOR));
    } catch (_error) {
      return directChildren(state.drawer, '[data-axa-shell-node]');
    }
  };

  const removeShell = () => shellNodes().forEach((node) => node.remove());

  const lockPageScroll = () => {
    if (!config().lockScroll || state.lockedByAxa || !document.body) return;
    if (document.body.style.overflow === 'hidden') return;

    state.bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    state.lockedByAxa = true;
  };

  const unlockPageScroll = () => {
    if (!state.lockedByAxa || !document.body) return;

    document.body.style.overflow = state.bodyOverflow;
    state.bodyOverflow = '';
    state.lockedByAxa = false;
  };

  const accordionParts = (item) => {
    const trigger = firstDirectChild(item, '[data-axa-accordion-trigger]');
    return {
      content: trigger ? trigger.nextElementSibling : null,
      trigger
    };
  };

  const setAccordionOpen = (item, shouldOpen) => {
    const parts = accordionParts(item);
    if (!parts.trigger || !parts.content) return;

    item.classList.toggle('is-open', shouldOpen);
    parts.trigger.setAttribute('aria-expanded', String(shouldOpen));
    parts.content.hidden = !shouldOpen;
  };

  const closeAccordionSiblings = (item) => {
    const panel = item.closest('[data-axa-panel]');
    const tabName = panel ? panel.dataset.axaPanel : '';
    const singleOpen = tabName === 'tab2' ? config().singleOpenTab2 : config().singleOpenTab1;
    if (!singleOpen || !item.parentElement) return;

    directChildren(item.parentElement, '[data-axa-menu-item]').forEach((sibling) => {
      if (sibling !== item) setAccordionOpen(sibling, false);
    });
  };

  const resetAccordions = () => {
    if (!state.drawer) return;

    state.drawer.querySelectorAll('[data-axa-menu-item]').forEach((item) => {
      setAccordionOpen(item, toBoolean(item.dataset.defaultOpen));
    });
  };

  const panelFor = (tabName) => state.drawer
    ? state.drawer.querySelector(`:scope > [data-axa-panel="${tabName}"]`)
    : null;

  const activateTab = (tabName, options = {}) => {
    if (!state.drawer) return;

    const requested = tabName === 'tab2' ? 'tab2' : 'tab1';
    const tabs = Array.from(state.drawer.querySelectorAll(':scope > .axa-mobile-tabs [data-axa-tab]'));
    const activeButton = tabs.find((tab) => tab.dataset.axaTab === requested) || tabs[0];
    const activeName = activeButton ? activeButton.dataset.axaTab : requested;
    const activePanel = panelFor(activeName);

    tabs.forEach((tab) => {
      const selected = tab === activeButton;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    state.drawer.querySelectorAll(':scope > [data-axa-panel]').forEach((panel) => {
      const selected = panel === activePanel;
      panel.classList.toggle('is-active', selected);
      panel.hidden = !selected;
      panel.setAttribute('aria-hidden', String(!selected));
      if (selected && options.resetScroll) panel.scrollTop = 0;
    });

    state.activeTab = activeName;
    rememberTab(activeName);
    if (options.focus && activeButton) activeButton.focus();
  };

  const preferredTab = () => rememberedTab() || config().defaultTab;

  const prepareForOpen = (forceReset = false) => {
    if (!state.drawer) return;

    const shouldReset = forceReset || config().resetOnOpen || !state.activeTab;
    if (shouldReset) resetAccordions();

    const nextTab = shouldReset ? preferredTab() : state.activeTab;
    activateTab(nextTab, { resetScroll: shouldReset });
    lockPageScroll();
  };

  const focusLastOpener = () => {
    const opener = state.lastOpener;
    if (!opener || !document.contains(opener) || typeof opener.focus !== 'function') return;

    window.setTimeout(() => {
      if (!state.drawer || state.drawer.contains(document.activeElement) || document.activeElement === document.body) {
        opener.focus();
      }
    }, 80);
  };

  const closeDrawer = () => {
    const nativeClose = document.querySelector('.t4s-drawer-menu__close[data-drawer-close], [data-drawer-close][aria-label*="lose"]');

    if (nativeClose && !nativeClose.hasAttribute('data-axa-close')) nativeClose.click();
    else if (state.drawer) state.drawer.setAttribute('aria-hidden', 'true');

    unlockPageScroll();
    focusLastOpener();
  };

  const openNativeCart = (customTrigger) => {
    const header = document.querySelector('#shopify-section-header, .t4s-header, header');
    const nativeCart = header && header.querySelector('[data-drawer-options*="mini_cart"], [data-drawer-options*="mini-cart"], a[href*="/cart"]');
    const href = customTrigger ? customTrigger.getAttribute('href') : '';

    closeDrawer();
    if (nativeCart && nativeCart !== customTrigger) nativeCart.click();
    else if (href) window.location.assign(href);
  };

  const countText = (root, selectors) => {
    if (!root) return '';

    for (const selector of selectors) {
      const element = root.querySelector(selector);
      if (!element) continue;
      const match = element.textContent.trim().match(/\d+/);
      if (match) return match[0];
    }

    return '';
  };

  const syncCounts = () => {
    state.countFrame = 0;
    if (!state.drawer) return;

    const header = document.querySelector('#shopify-section-header, .t4s-header, header');
    const cartCount = countText(header, ['[data-cart-count]', '.t4s-cart-count', '[data-cart-items-count]']);
    const wishlistCount = countText(header, ['[data-count-wishlist]', '.t4s-wis-count', '[data-wishlist-count]']);

    state.drawer.querySelectorAll('[data-axa-cart-count]').forEach((node) => {
      node.textContent = cartCount || node.textContent.trim() || '0';
    });
    state.drawer.querySelectorAll('[data-axa-wishlist-count]').forEach((node) => {
      node.textContent = wishlistCount || '0';
    });
  };

  const scheduleCountSync = () => {
    if (state.countFrame) return;
    state.countFrame = window.requestAnimationFrame(syncCounts);
  };

  const observeHeader = () => {
    const header = document.querySelector('#shopify-section-header, .t4s-header, header');
    if (!header || header === state.headerObserved) return;

    if (state.headerObserver) state.headerObserver.disconnect();
    state.headerObserved = header;
    state.headerObserver = new MutationObserver(scheduleCountSync);
    state.headerObserver.observe(header, { childList: true, characterData: true, subtree: true });
  };

  const onDrawerClick = (event) => {
    const tab = event.target.closest('[data-axa-tab]');
    if (tab && state.drawer && state.drawer.contains(tab)) {
      event.preventDefault();
      activateTab(tab.dataset.axaTab);
      return;
    }

    const accordion = event.target.closest('[data-axa-accordion-trigger]');
    if (accordion && state.drawer && state.drawer.contains(accordion)) {
      event.preventDefault();
      const item = accordion.parentElement;
      const shouldOpen = accordion.getAttribute('aria-expanded') !== 'true';
      if (shouldOpen) closeAccordionSiblings(item);
      setAccordionOpen(item, shouldOpen);
      return;
    }

    const close = event.target.closest('[data-axa-close]');
    if (close && state.drawer && state.drawer.contains(close)) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const cart = event.target.closest('[data-axa-cart-trigger]');
    if (cart && state.drawer && state.drawer.contains(cart)) {
      event.preventDefault();
      openNativeCart(cart);
    }
  };

  const onDrawerKeydown = (event) => {
    const tab = event.target.closest('[data-axa-tab]');
    if (!tab || !state.drawer || !state.drawer.contains(tab)) return;

    const tabs = Array.from(state.drawer.querySelectorAll(':scope > .axa-mobile-tabs [data-axa-tab]'));
    const currentIndex = tabs.indexOf(tab);
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    activateTab(tabs[nextIndex].dataset.axaTab, { focus: true });
  };

  const bindDrawer = () => {
    if (!state.drawer || state.drawer === state.drawerBound) return;

    if (state.drawerBound) {
      state.drawerBound.removeEventListener('click', onDrawerClick);
      state.drawerBound.removeEventListener('keydown', onDrawerKeydown);
    }

    state.drawer.addEventListener('click', onDrawerClick);
    state.drawer.addEventListener('keydown', onDrawerKeydown);
    state.drawerBound = state.drawer;
  };

  const observeDrawer = () => {
    if (!state.drawer || state.drawer === state.drawerObserved) return;
    if (state.drawerObserver) state.drawerObserver.disconnect();

    state.drawerObserved = state.drawer;
    state.lastHidden = state.drawer.getAttribute('aria-hidden') || 'true';
    state.drawerObserver = new MutationObserver((mutations) => {
      if (!isMobile() || !state.template) return;
      if (mutations.some((mutation) => mutation.type === 'childList')) ensureShell();
      if (!mutations.some((mutation) => mutation.attributeName === 'aria-hidden')) return;

      const hidden = state.drawer.getAttribute('aria-hidden') || 'true';
      if (hidden === state.lastHidden) return;
      state.lastHidden = hidden;

      if (hidden === 'false') prepareForOpen(false);
      else {
        unlockPageScroll();
        focusLastOpener();
      }
    });
    state.drawerObserver.observe(state.drawer, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true
    });
  };

  const ensureShell = (force = false) => {
    if (!state.drawer || !state.template || !state.template.content) return false;

    const expectedCount = state.template.content.children.length;
    const currentCount = shellNodes().length;
    const needsClone = force || expectedCount !== currentCount || currentCount === 0;
    const tabToRestore = force ? preferredTab() : state.activeTab || preferredTab();

    if (needsClone) {
      removeShell();
      state.drawer.append(state.template.content.cloneNode(true));
      state.activeTab = null;
    }

    const footer = firstDirectChild(state.drawer, '.axa-mobile-footer');
    state.drawer.classList.add('axa-mobile-menu');
    state.drawer.classList.toggle('axa-mobile-no-footer', !footer || !footer.querySelector('a'));
    state.drawer.dataset.axaHasClose = String(config().showClose);
    state.drawer.dataset.axaReady = 'true';

    bindDrawer();
    if (needsClone) {
      resetAccordions();
      activateTab(tabToRestore, { resetScroll: true });
      if (isOpen()) lockPageScroll();
    }
    scheduleCountSync();
    return needsClone;
  };

  const cleanup = () => {
    unlockPageScroll();
    if (!state.drawer) return;

    removeShell();
    state.drawer.classList.remove('axa-mobile-menu', 'axa-mobile-no-footer');
    delete state.drawer.dataset.axaHasClose;
    delete state.drawer.dataset.axaReady;
    state.activeTab = null;
  };

  function refresh(forceClone = false) {
    const nextTemplate = findTemplate();
    const nextDrawer = findDrawer();
    const templateChanged = nextTemplate !== state.template;
    const drawerChanged = nextDrawer !== state.drawer;

    state.template = nextTemplate;
    state.drawer = nextDrawer;

    if (!state.template || !state.drawer) {
      cleanup();
      return;
    }

    installMediaQuery();
    if (!isMobile()) {
      cleanup();
      return;
    }

    observeDrawer();
    observeHeader();
    ensureShell(forceClone || templateChanged || drawerChanged);

    if (isOpen()) prepareForOpen(forceClone || templateChanged || drawerChanged);
  }

  const onDocumentClick = (event) => {
    const opener = event.target.closest('[data-drawer-options*="#t4s-menu-drawer"], [data-drawer-options*="t4s-menu-drawer"]');
    if (opener) state.lastOpener = opener;

  };

  const onDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !isMobile() || !isOpen()) return;
    event.preventDefault();
    closeDrawer();
  };

  const onSectionRefresh = (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    if (sectionId && state.template && sectionId !== config().sectionId) return;
    window.setTimeout(() => refresh(true), 0);
  };

  const onBlockSelect = (event) => {
    if (!state.drawer || !isMobile()) return;
    const target = event.target && event.target.closest ? event.target.closest('[data-axa-panel]') : null;
    if (target && target.dataset.axaPanel) activateTab(target.dataset.axaPanel);
  };

  const start = () => {
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onDocumentKeydown);
    document.addEventListener('shopify:section:load', onSectionRefresh);
    document.addEventListener('shopify:section:reorder', onSectionRefresh);
    document.addEventListener('shopify:block:select', onBlockSelect);
    COUNT_EVENTS.forEach((eventName) => document.addEventListener(eventName, scheduleCountSync));
    window.addEventListener('pageshow', () => refresh(false));
    refresh(false);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

